import { supabase } from "./supabase";

// Leitura e escrita do perfil do aluno.
//
// A escrita passa por RPC `security definer` (000042), nunca por `update` na
// tabela — o cliente não tem esse privilégio, e a tentativa volta 403. Não é
// zelo: `profiles` guarda o e-mail que amarra o aluno às compras dele e o CPF
// que vai em contrato, e nenhum dos dois é campo de formulário.

export type Redes = {
  instagram?: string;
  linkedin?: string;
  youtube?: string;
  tiktok?: string;
  site?: string;
};

export type MeuPerfil = {
  nome: string | null;
  email: string;
  cpf: string | null;
  telefone: string | null;
  bio: string | null;
  redes: Redes;
  avatar_url: string | null;
  /**
   * RG e nascimento entraram na 000137, e existem por causa do CERTIFICADO da
   * pós: a trava 1 de `sec_pode_solicitar` exige nome, CPF, telefone, RG e
   * nascimento para o certificado, e os três primeiros para as declarações. A
   * lista saiu do payload do n8n que gera o PDF, não de uma escolha nossa.
   *
   * Medido em 31/08: o hub tinha CPF em 204 de 39.252 matriculados e NENHUMA
   * coluna de RG ou nascimento. Sem estes campos ninguém pediria documento.
   */
  rg: string | null;
  nascimento: string | null;
};

/** As redes que a tela oferece, na ordem em que aparecem. */
export const REDES: Array<{
  chave: keyof Redes;
  rotulo: string;
  /** Prefixo com que o front monta o link. O banco guarda só o handle. */
  prefixo: string;
  exemplo: string;
}> = [
  { chave: "instagram", rotulo: "Instagram", prefixo: "https://instagram.com/", exemplo: "seu.usuario" },
  { chave: "linkedin", rotulo: "LinkedIn", prefixo: "https://linkedin.com/in/", exemplo: "seu-nome" },
  { chave: "youtube", rotulo: "YouTube", prefixo: "https://youtube.com/@", exemplo: "seucanal" },
  { chave: "tiktok", rotulo: "TikTok", prefixo: "https://tiktok.com/@", exemplo: "seu.usuario" },
];

export async function carregarMeuPerfil(): Promise<MeuPerfil | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("nome, email, cpf, telefone, bio, redes, avatar_url, rg, nascimento")
    .limit(1);
  if (error || !data || data.length === 0) return null;
  const p = data[0] as MeuPerfil;
  return { ...p, redes: (p.redes ?? {}) as Redes };
}

export type ResultadoSalvar =
  | { ok: true; perfil: MeuPerfil }
  | { ok: false; mensagem: string };

export async function salvarMeuPerfil(campos: {
  nome?: string;
  telefone?: string;
  bio?: string;
  redes?: Redes;
  /**
   * CPF e RG são de PREENCHIMENTO ÚNICO (decisão de Victor, 31/08): o aluno
   * preenche o que está vazio e depois não edita mais. Quem recusa a troca é o
   * banco, com mensagem — e reenviar o MESMO valor não é troca, o que é o que
   * permite esta função mandar o formulário inteiro (000139).
   *
   * Vão como TEXTO com máscara, e a conversão para dígitos é do BANCO. É o
   * inverso da regra da casa ("só o número viaja") de propósito: aqui a
   * conversão tem de ficar ao lado da validação de dígito verificador, senão
   * duas normalizações discordariam sobre o que é o mesmo CPF — e foi
   * exatamente isso que quebrou na 000138, quando a comparação normalizava de um
   * jeito e a gravação de outro.
   */
  cpf?: string;
  rg?: string;
  /** ISO `aaaa-mm-dd`. O `<input type="date">` já entrega nesse formato. */
  nascimento?: string;
}): Promise<ResultadoSalvar> {
  const { data, error } = await supabase.rpc("atualizar_meu_perfil", {
    p_nome: campos.nome ?? null,
    p_telefone: campos.telefone ?? null,
    p_bio: campos.bio ?? null,
    p_redes: campos.redes ?? null,
    // Vazio vira NULO, e a diferença importa: nulo significa "não mexe" (o
    // contrato da 000042). A função já tem a guarda `btrim(p_cpf) <> ''`, então
    // string vazia não faria estrago — mas mandar `""` diria "o aluno preencheu
    // o CPF com nada", e a intenção certa é "o aluno nem tocou nesse campo".
    p_cpf: campos.cpf?.trim() ? campos.cpf : null,
    p_rg: campos.rg?.trim() ? campos.rg : null,
    p_nascimento: campos.nascimento?.trim() ? campos.nascimento : null,
  });

  if (error) {
    // A RPC devolve mensagem escrita para o aluno ler (nome curto, rede
    // inválida). Repassar o texto do Postgres é melhor que "erro ao salvar",
    // porque ele diz o que corrigir. O fallback existe para falha de rede, onde
    // a mensagem do driver não serve para ninguém.
    return {
      ok: false,
      mensagem: error.message?.trim()
        ? error.message
        : "Não foi possível salvar agora. Tente de novo em instantes.",
    };
  }
  const p = data as MeuPerfil;
  return { ok: true, perfil: { ...p, redes: (p.redes ?? {}) as Redes } };
}

/**
 * Reduz a imagem escolhida a um quadrado de 512px antes de subir.
 *
 * O aluno manda foto de celular: 3 ou 4 mil pixels e vários megabytes, para
 * aparecer num círculo de 44px no header. Sem reduzir aqui, o bucket estoura o
 * limite de 2 MB (e a foto que passa deixa o header lento em rede ruim). O
 * recorte é central, que é onde o rosto costuma estar num retrato.
 */
async function reduzirParaQuadrado(arquivo: File, lado = 512): Promise<Blob> {
  const bitmap = await createImageBitmap(arquivo);
  const corte = Math.min(bitmap.width, bitmap.height);
  const x = (bitmap.width - corte) / 2;
  const y = (bitmap.height - corte) / 2;

  const canvas = document.createElement("canvas");
  canvas.width = lado;
  canvas.height = lado;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("sem canvas");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, x, y, corte, corte, 0, 0, lado, lado);
  bitmap.close();

  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.9));
  if (!blob) throw new Error("falha ao converter a imagem");
  return blob;
}

export type ResultadoFoto = { ok: true; url: string | null } | { ok: false; mensagem: string };

/**
 * Sobe a foto para a pasta do próprio aluno e aponta o perfil para ela.
 *
 * O nome do arquivo leva um sufixo de tempo: sobrescrever `avatar.jpg` deixaria a
 * foto antiga em cache no navegador e no CDN, e o aluno trocaria a imagem sem ver
 * diferença. Arquivo novo, URL nova, cache irrelevante.
 */
export async function enviarMinhaFoto(arquivo: File): Promise<ResultadoFoto> {
  try {
    const { data: sessao } = await supabase.auth.getSession();
    const uid = sessao.session?.user.id;
    if (!uid) return { ok: false, mensagem: "Sua sessão expirou. Entre de novo." };

    if (!/^image\/(jpeg|png|webp)$/.test(arquivo.type)) {
      return { ok: false, mensagem: "Envie uma imagem JPG, PNG ou WEBP." };
    }

    const blob = await reduzirParaQuadrado(arquivo);
    const caminho = `${uid}/avatar-${Date.now()}.jpg`;

    const { error: erroUpload } = await supabase.storage
      .from("avatares")
      .upload(caminho, blob, { contentType: "image/jpeg", upsert: true });
    if (erroUpload) {
      return { ok: false, mensagem: "Não conseguimos enviar a imagem. Tente de novo." };
    }

    // A RPC recebe o CAMINHO, não a URL: é ela que monta o endereço, e assim o
    // avatar não pode apontar para fora do nosso Storage.
    const { data, error } = await supabase.rpc("definir_meu_avatar", { p_caminho: caminho });
    if (error) return { ok: false, mensagem: "A imagem subiu, mas não foi possível aplicá-la." };

    return { ok: true, url: (data as MeuPerfil).avatar_url };
  } catch {
    return { ok: false, mensagem: "Não conseguimos preparar a imagem. Tente outra foto." };
  }
}

export async function removerMinhaFoto(): Promise<ResultadoFoto> {
  const { error } = await supabase.rpc("definir_meu_avatar", { p_caminho: null });
  if (error) return { ok: false, mensagem: "Não foi possível remover a foto agora." };
  return { ok: true, url: null };
}

/** Máscara de telefone brasileiro conforme o aluno digita. */
export function formatarTelefone(valor: string): string {
  const d = valor.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/**
 * CPF sempre parcialmente oculto.
 *
 * A tela mostra o CPF para o aluno confirmar que é o dele — e para isso os
 * últimos dígitos bastam. Imprimir os onze inteiros numa página que ele pode
 * abrir em qualquer lugar (e que vai numa captura de tela para o suporte) expõe
 * dado que a tela não precisa expor.
 */
export function cpfParcial(cpf: string | null): string | null {
  if (!cpf) return null;
  const d = cpf.replace(/\D/g, "");
  if (d.length !== 11) return cpf;
  return `•••.•••.${d.slice(6, 9)}-${d.slice(9)}`;
}
